import React, { useState, useEffect } from 'react';
import { X, TrendingUp, TrendingDown, Calendar } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MetricDetailModal = ({ metricKey, metricName, onClose }) => {
  const [periodo, setPeriodo] = useState('7d');
  const [comparativa, setComparativa] = useState('semanal');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistorico();
  }, [periodo, metricKey]);

  const fetchHistorico = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/analytics/historico/${metricKey}?periodo=${periodo}`,
        {
          credentials: 'include'
        }
      );

      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        console.error('Error al cargar histórico');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatChartData = (datos) => {
    if (!datos) return [];
    return datos.map(d => ({
      ...d,
      fechaFormateada: format(parseISO(d.fecha), 'dd MMM', { locale: es })
    }));
  };

  const getWeeklyData = () => {
    if (!data?.datos) return [];
    // Últimos 7 días
    return formatChartData(data.datos.slice(-7));
  };

  const getMonthlyData = () => {
    if (!data?.datos) return [];
    // Todos los datos disponibles para vista mensual/histórica
    return formatChartData(data.datos);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-800 border border-gray-700 rounded-lg p-3 shadow-lg">
          <p className="text-white font-semibold">{payload[0].payload.fechaFormateada}</p>
          <p className="text-yellow-500">
            Valor: <span className="font-bold">{payload[0].value}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-slate-900 border-b border-gray-700 p-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-yellow-500">{metricName}</h2>
            <p className="text-gray-400 text-sm">Análisis histórico y comparativas</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
              <p className="text-gray-400">Cargando datos históricos...</p>
            </div>
          ) : (
            <>
              {/* Period Selector */}
              <div className="mb-6 flex flex-wrap gap-3">
                <button
                  onClick={() => setPeriodo('7d')}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    periodo === '7d'
                      ? 'bg-yellow-500 text-black font-semibold'
                      : 'bg-slate-800 text-gray-400 hover:bg-slate-700'
                  }`}
                >
                  7 días
                </button>
                <button
                  onClick={() => setPeriodo('30d')}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    periodo === '30d'
                      ? 'bg-yellow-500 text-black font-semibold'
                      : 'bg-slate-800 text-gray-400 hover:bg-slate-700'
                  }`}
                >
                  30 días
                </button>
                <button
                  onClick={() => setPeriodo('3m')}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    periodo === '3m'
                      ? 'bg-yellow-500 text-black font-semibold'
                      : 'bg-slate-800 text-gray-400 hover:bg-slate-700'
                  }`}
                >
                  3 meses
                </button>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-800 rounded-lg p-4 border border-gray-700">
                  <p className="text-gray-400 text-sm mb-1">Total del Período</p>
                  <p className="text-3xl font-bold text-white">{data?.resumen?.total || 0}</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-4 border border-gray-700">
                  <p className="text-gray-400 text-sm mb-1">Promedio Diario</p>
                  <p className="text-3xl font-bold text-white">{data?.resumen?.promedio_diario || 0}</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-4 border border-gray-700">
                  <p className="text-gray-400 text-sm mb-1">Cambio Semanal</p>
                  <div className="flex items-center">
                    <p className={`text-3xl font-bold ${
                      (data?.resumen?.cambio_semanal_porcentaje || 0) >= 0 
                        ? 'text-green-500' 
                        : 'text-red-500'
                    }`}>
                      {data?.resumen?.cambio_semanal_porcentaje || 0}%
                    </p>
                    {(data?.resumen?.cambio_semanal_porcentaje || 0) >= 0 ? (
                      <TrendingUp className="w-6 h-6 text-green-500 ml-2" />
                    ) : (
                      <TrendingDown className="w-6 h-6 text-red-500 ml-2" />
                    )}
                  </div>
                </div>
              </div>

              {/* Comparativa Selector */}
              <div className="mb-4">
                <label className="text-gray-400 text-sm mb-2 block">Vista Comparativa:</label>
                <select
                  value={comparativa}
                  onChange={(e) => setComparativa(e.target.value)}
                  className="bg-slate-800 text-white border border-gray-700 rounded-lg px-4 py-2"
                >
                  <option value="semanal">Semanal (Barras)</option>
                  <option value="mensual">Mensual (Líneas)</option>
                </select>
              </div>

              {/* Charts */}
              {comparativa === 'semanal' ? (
                <div className="bg-slate-800 rounded-lg p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <Calendar className="w-5 h-5 mr-2 text-yellow-500" />
                    Últimos 7 Días (Gráfica de Barras)
                  </h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={getWeeklyData()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis 
                        dataKey="fechaFormateada" 
                        stroke="#9CA3AF"
                        tick={{ fill: '#9CA3AF' }}
                      />
                      <YAxis 
                        stroke="#9CA3AF"
                        tick={{ fill: '#9CA3AF' }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar 
                        dataKey="valor" 
                        fill="#EAB308" 
                        radius={[8, 8, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="bg-slate-800 rounded-lg p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2 text-yellow-500" />
                    Tendencia Completa (Gráfica de Líneas)
                  </h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={getMonthlyData()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis 
                        dataKey="fechaFormateada" 
                        stroke="#9CA3AF"
                        tick={{ fill: '#9CA3AF' }}
                      />
                      <YAxis 
                        stroke="#9CA3AF"
                        tick={{ fill: '#9CA3AF' }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Line 
                        type="monotone" 
                        dataKey="valor" 
                        stroke="#EAB308" 
                        strokeWidth={3}
                        dot={{ fill: '#EAB308', r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Period Info */}
              <div className="mt-6 bg-slate-800 rounded-lg p-4 border border-gray-700">
                <p className="text-gray-400 text-sm">
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Período analizado: <span className="text-white font-semibold">
                    {data?.fecha_inicio} - {data?.fecha_fin}
                  </span>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetricDetailModal;
